import { CommonDialogs } from "@mcleod/common";
import { Button, ButtonVariant, ChangeEvent, ClickEvent, Component, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownSelectionEvent, ForcedCase, Label, Layout, LookupModelSearchEvent, OpenType, Panel, SlideoutDecorator, Switch, TableRowMode, Textbox } from "@mcleod/components";
import { Api, ArrayUtil, CityUtil, DateRange, HorizontalAlignment, McLeodClassicIntegration, Model, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { LocationToProspect } from "@mcleod/crm";
import { getDetentionControlBoolean, TableComment, UsersResponsibilitySlideoutMF } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutLocation } from "./autogen/AutogenLayoutLocation";
import { CallRecordTable } from "./CallRecordTable";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";
import { GoogleSearchDecorator, GoogleSearchDecoratorProps } from "./GoogleSearchDecorator";

const viewLocationURL = "lme/dispatch/Location?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "location",
    screenName: "Location",
    navigateToUrl: viewLocationURL
};

const GOOGLE_SEARCH_PROPS: GoogleSearchDecoratorProps = {
    cityField: 'city_name',
    cityIdField: 'city_id',
    stateField: 'state',
    zipField: 'zip_code'
}

export class Location extends AutogenLayoutLocation {
    buttonOpenClassic: any;
    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;
    dialogTextboxProps = { forcedCase: ForcedCase.UPPER, tooltip: "Input will be truncated to eight characters" };

    private _locationName: string;
    private _locationAdress: string;
    private _locationAdress2: string;
    private _locationCity: string;
    private _locationState: string;
    private _locationZipCode: string;
    private _locationCityID: string;
    private _locationContactName: string;
    private _locationContactPhone: string;

    override async onLoad() {
        new GoogleSearchDecorator(this, GOOGLE_SEARCH_PROPS);
        this.layoutContactTable.parentRowType = "L";
        this.addChangeListenerToSourceLocation();

        await Model.searchSingleRecord("lme/powerbroker/br-tracking-ctrl", { vendor: "M" }).then(response => {
            this.tabIntegration.visible = response?.get("is_active") == "Y";
        });

        (this.layoutTableComment as TableComment).displayCopyOrderFields = true;
        this.sourceLocation.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceLocationAfterGetDataboundValues(row, dataSource);

        this.mapLocation.addPinPlotListener(() => {
            const row = this.sourceLocation.activeRow;
            if (row.get("google_place_id") != null && row.get("latitude") != null && row.get("longitude") != null) {
                this.mapLocation.fitPins();
                this.mapLocation.setZoom(14);
            }
        });
        this.layoutCallRecord.locationId = null;

        // Augment beforePost callback added by DuplicateId
        const originalBeforePost = this.mainDataSource.doBeforeDataSourcePost;
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (!(await DuplicateId.validateBeforePost(this.textId, DUPLICATE_VALIDATION_PROPS))) {
                return false;
            }
            if(this.activeRow.get("load_unload_excl") == "N" && !this.activeRow.isNull("id")){
                const resp = await this.setLoadUnloadTimes();
            }
            if (originalBeforePost) {
                return originalBeforePost();
            } else {
                return true;
            }
        }

        this.tabDetention.visible = getDetentionControlBoolean("is_active", false);
    }

    private addChangeListenerToSourceLocation() {
        this.sourceLocation.addAfterModeChangeListener(() => {
            if (this.sourceLocation.mode == DataSourceMode.ADD) {
                this.getDriverLoadUnloadDescr(this.textboxDriverLoadId).then(() => {
                    if (this.activeRow?.get("driver_load_id") == this.activeRow?.get("driver_unload_id")) {
                        this.activeRow.setLookupModelData("driver_unload_id", this.activeRow?.getLookupModelData("driver_load_id"));
                        this.textboxDriverUnloadId.displayData(this.activeRow, null, 0);
                    }
                    else {
                        this.getDriverLoadUnloadDescr(this.textboxDriverUnloadId);
                    }
                });
                this.textboxLoadUnloadCount.enabled = this.enableLoadCount("A");
            }
            if (this.sourceLocation.mode == DataSourceMode.UPDATE) {
                const placeId = this.activeRow.get("google_place_id", null);
                this.imageGooglePlaces.visible = StringUtil.isEmptyString(placeId);
            }
        });
    }

    imageGooglePlacesOnClick(event: ClickEvent) {
        this.textboxGooglePlacesSearch.visible = true;
        const name = this.activeRow.get("name");
        const city = this.activeRow.get("city_name");
        const state = this.activeRow.get("state");
        const zip = this.activeRow.get("zip_code");
        const searchParts = [name, city, state, zip].filter(part => !StringUtil.isEmptyString(part));
        const searchText = searchParts.join(" ");
        if (!StringUtil.isEmptyString(searchText) && StringUtil.isEmptyString(this.textboxGooglePlacesSearch.text)) {
            this.textboxGooglePlacesSearch.text = searchText;
            this.textboxGooglePlacesSearch.showLookupModelDropdown(searchText);
        }
    }

    textboxGooglePlaceIdOnChange(event: ChangeEvent) {
        const isSearchMode = this.sourceLocation.mode === DataSourceMode.SEARCH;
        if (isSearchMode) {
            this.imageGooglePlaces.visible = false;
            return
        }
        const googlePlaceId = this.activeRow?.get("google_place_id");
        this.imageGooglePlaces.visible = StringUtil.isEmptyString(googlePlaceId);
    }

    private async setLoadUnloadTimes(){
        const response = await Api.post("lme/dispatch/calc-location-load-times", {
            location_id: this.activeRow.get("id"),
            load_unload_param: this.activeRow.get("load_unload_param"),
            load_unload_count: this.activeRow.get("load_unload_count")
        });
        const data = ArrayUtil.isEmptyArray(response?.data) !== true ? response.data[0] : null;
        if(data != null){
            this.activeRow.set("loading_time", data.loading_time);
            this.activeRow.set("unloading_time", data.unloading_time);
        }
    }

    sourceLocationAfterModeChange(event){
        this.displayAddLocationData();

        const isNotSearchMode = event.newMode !== DataSourceMode.SEARCH;
        this.buttonAllCalls.visible = isNotSearchMode;
    }

    //called from the tender cross reference slideout only
    setAddLocationData(name: string, address: string, address2: string, city: string,
                        state: string, zip: string, cityID: string,
                        contactName: string, contactPhone: string){
        this._locationName = name;
        this._locationAdress = address;
        this._locationAdress2 = address2;
        this._locationCity = city;
        this._locationState = state;
        this._locationZipCode = zip;
        this._locationCityID = cityID;
        this._locationContactName = contactName;
        this._locationContactPhone = contactPhone;
        this.displayAddLocationData();
    }

    private displayAddLocationData(){
        if(this._locationCityID != undefined && this.sourceLocation?.mode === DataSourceMode.ADD){
            this.mainDataSource.activeRow?.set("name", this._locationName);
            this.mainDataSource.activeRow?.set("address1", this._locationAdress);
            this.mainDataSource.activeRow?.set("address2", this._locationAdress2);
            this.mainDataSource.activeRow?.set("city_name", this._locationCity);
            this.mainDataSource.activeRow?.set("state", this._locationState);
            this.mainDataSource.activeRow?.set("zip_code", this._locationZipCode);
            this.mainDataSource.activeRow?.set("city_id", this._locationCityID);

            const contactName = this._locationContactName;
            const contactPhone = this._locationContactPhone;

            if(!StringUtil.isEmptyString(contactName) || !StringUtil.isEmptyString(contactPhone)) {
                this.mainDataSource.activeRow?.set("contact.name", this._locationContactName);
                this.mainDataSource.activeRow?.set("contact.phone", this._locationContactPhone);

                //the contact layout is imbeded. Must create model row then addRow to set data on the table
                const contactModelRow = new ModelRow("lme/general/contact", true, {name: this._locationContactName,
                                                                                    phone: this._locationContactPhone,
                                                                                    is_active: "Y"});
                this.layoutContactTable.tableContacts.addRow(contactModelRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });
            }

            this.textboxName.text = this.mainDataSource.activeRow?.get("name");
            this.textboxAddress1.text = this.mainDataSource.activeRow?.get("address1");
            this.textboxAddress2.text = this.mainDataSource.activeRow?.get("address2");
            const cityName = this.mainDataSource.activeRow?.get("city_name") as string;
            this.textboxCity.textCombined.text = CityUtil.formatCityStateZip(cityName.toUpperCase(),
                                                                              this.mainDataSource.activeRow?.get("state"),
                                                                              this.mainDataSource.activeRow?.get("zip_code"));
        }
    }

    displayLocationUsersSlideout() {
        const urs = UsersResponsibilitySlideoutMF.builder()
                .layoutName("lme/dispatch/LocationUsers")
                .title(this.getLocationUsersSlideoutTitle())
                .idFilter(this.getLocationUsersIdFilter())
                .parentMainDataSource(this.sourceLocation)
                .parentAgencyLinkDataSource(this.sourceAgencyLink)
                .primaryAgencyField(this.getLocationUsersAgencyField())
                .build();
        urs.show();
    }

    private getLocationUsersSlideoutTitle(): string {
        const name: string = this.sourceLocation.activeRow?.get("name");
        return `Location Users${StringUtil.isEmptyString(name) ? "" : " - " + name}`;
    }

    private getLocationUsersIdFilter(): any {
        const locationId: string = this.sourceLocation.activeRow?.get("id");
        return locationId != null ? { id: locationId } : null;
    }

    private getLocationUsersAgencyField(): string {
        return "primary_agency";
    }

    /** This is an event handler for the beforeExecution event of sourceLocation.  It's gross. */
    sourceLocationAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getLocationUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    async getDriverLoadUnloadDescr(component: Textbox) {
        const unloadId = this.sourceLocation.activeRow?.get(component.field);
        if (StringUtil.isEmptyString(unloadId))
            return;

        await Api.search("lme/dispatch/load-unload", { id: unloadId }).then(response => {
            this.mainDataSource.activeRow.setLookupModelData(component.field, response?.data[0]);
            component.displayData(this.mainDataSource.activeRow, null, 0);
        });
    }

    textboxAssignedToBeforeLookupModelSearch(event) {
        event.filter.web_user_type = 'U';
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.dsp.EntryLocation", this.mainDataSource.activeRow.get("id"));
    }

    buttonConvertToProspectOnClick(event: ClickEvent) {
        LocationToProspect.showAsSlideout([this.mainDataSource.activeRow], this.buttonConvertToProspect);
    }

    sourceLocationBeforeModeChange(event) {
        this.buttonOpenClassic.visible = event.newMode === DataSourceMode.UPDATE;

        if (event.newMode === DataSourceMode.ADD) {
            this.textId.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "location" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                if (this.codeGenerationIsActive) {
                    this.textId.caption = "*Location Code";
                    this.textId.enabledDuringAdd = this.allowManualEntry;
                }
                DuplicateId.configureIdTextbox(this.textId, {
                    ...DUPLICATE_VALIDATION_PROPS,
                    codeGenerationIsActive: this.codeGenerationIsActive
                });
            });
        }
    }

    buttonAllCallsOnClick(event: ClickEvent) {
        const id = this.activeRow?.get("id");
        if (!StringUtil.isEmptyString(id)) {
            const layout = Layout.getLayout("lme/dispatch/CallRecordTable") as CallRecordTable;
            new SlideoutDecorator({
                layout: layout,
                width: window.innerWidth * .75,
                fillVerticalSpace: true,
                title: `Location Sales Calls - ${this.activeRow.get("name")}`,
                layoutLoadListeners: (event) => layout.tableCallRecords.headerVisible = true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ "parent_row_type": "L", "parent_row_id": id })
            })
        }
    }

    /** This is an event handler for the afterExecution event of sourceLocation.  */
    sourceLocationAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            (this.layoutCallRecord as CallRecordTable).locationId = this.activeRow?.get("id");
            const excludeDwell = this.activeRow.get("load_unload_excl");
            excludeDwell == "N" || excludeDwell == undefined ? this.enableDwellFields(true): this.enableDwellFields(false);
        }

        if (event?.getAction() == DataSourceAction.UPDATE) {
            this.textId.enabled = false;
            this.textId.required = true;
        }

        if (event.getAction() == DataSourceAction.ADD) {
            this.textId.enabled = false;
        }

        this.layoutDetentionHistoryTable.tableDetentionHistory.allowAdvancedSearch = false;
        this.layoutDetentionHistoryTable.tableDetentionHistory.defaultOpenType = OpenType.NEW_TAB_WITH_SWITCH;

        const dateRange = DateRange.parseDateRangeWithKeywords("t-5,t").getFormattedString().split("-");
        const filter = {
            type: "L",
            stop_location_id: this.activeRow?.get("id"),
            stop_location_name: this.activeRow?.get("name"),
            status: "!V",
            creation_date: `>=${dateRange[0]}&<=${dateRange[1]}`
        };
        this.layoutDetentionHistoryTable.layoutDetentionSummary.setNewTabFilter(filter);
    }

    private duplicateLocationLinkOnClick(locationCode: string) {
        Location.navigateTo(locationCode, true);
    }

    public static navigateTo(id: string, newTab = true): void {
        Navigation.navigateTo(viewLocationURL + id, { newTab: newTab });
    }

    private getDuplicateLocationSnackbarPanel(locationId: string, description: string, autoGenerated: boolean): Panel {
        const labelProps = { color: "white", fontSize: "large", rowBreak: false, paddingLeft: 1, paddingRight: 1, marginLeft: 0, marginRight: 0 };

        const generatedString: string = autoGenerated ? " generated " : " ";
        let components: Component[] = [];
        if (!StringUtil.isEmptyString(locationId)) {
            components = [
                new Label({ ...labelProps, text: "The" + generatedString + "code [" }),
                new Label({ ...labelProps, text: locationId + " - " + description + " " }),
                new Button({ imageName: ImageName.detail, imageWidth: 20, imageHeight: 20, margin: 0, tooltip: "Click to go to Location screen", color: "subtle.light", variant: ButtonVariant.round, onClick: () => this.duplicateLocationLinkOnClick(locationId), rowBreak: false }),
                new Label({ ...labelProps, rowBreak: false, text: "] " }),
                new Label({ ...labelProps, text: "is a duplicate of an existing code.  Please enter a new one." })
            ];
        }
        return new Panel({ wrap: true, align: HorizontalAlignment.LEFT, fillRow: true, components: components, fillHeight: true });
    }

    showDuplicateCodeDialog(code: string, generated: boolean) {
        const generatedString: string = generated ? " generated " : " ";
        CommonDialogs.showInputDialog("Enter a new code", "Duplicate" + generatedString + "code [" + code + "]", this.dialogTextboxProps)
        .then(async (input) => {
            if (input) {
                input = input.substring(0, 8);
                this.textId.text = input;
                this.mainDataSource.activeRow.set("id", input);
                await this.checkManualCode()
                .then((response) => {
                    if (response.data[0]?.is_duplicate) {
                        this.showDuplicateCodeDialog(input, false);
                    }
                });
            } else {
                this.textId.text = null;
                this.mainDataSource.activeRow.set("id", null);
            }
        });
    }

    textboxCategoryBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.location = "Y";
    }

    async checkManualCode(): Promise<any> {
        return await Api.post("lme/dispatch/asset/check-manual-location-code",
            {
                "id": this.mainDataSource?.activeRow?.get("id")
            });
    }

    /** This is an event handler for the afterDropdownSelection event of textboxLoadUnloadParam. */
    textboxLoadUnloadParamAfterDropdownSelection(event: DropdownSelectionEvent) {
        if(event.selection != null){
            this.textboxLoadUnloadCount.enabled = this.enableLoadCount(event.selection.value);
            this.textboxLoadUnloadCount.resetValidation();
        }
    }

    private enableLoadCount(value: string): boolean{
        if (value == "D" || value == "S") {
            this.textboxLoadUnloadCount.required = true;
        }
        else {
            this.textboxLoadUnloadCount.clear();
            this.textboxLoadUnloadCount.required = false;
        }

        return value == "D" || value == "S";
    }

    /** This is an event handler for the onClick event of switchLoadUnloadExclude. */
    switchLoadUnloadExcludeOnClick(event: ClickEvent) {
        const excludeSwitch =  event.target as Switch;
        if(this.sourceLocation.mode != DataSourceMode.SEARCH){
            if(!excludeSwitch.checked){
                const paramItems = this.textboxLoadUnloadParam.items;
                this.textboxLoadUnloadParam.selectedItem = paramItems == undefined ? "A" : paramItems[0];
                this.textboxLoadUnloadParam.required = true;
                this.activeRow.set("load_unload_param", "A");
            }
            this.enableDwellFields(!excludeSwitch.checked);
        }
    }

    private enableDwellFields(value:boolean){
        const selectedItem = this.textboxLoadUnloadParam.selectedItem;
        this.textboxLoadUnloadCount.enabled = value && this.enableLoadCount(selectedItem == null ? "A" :  selectedItem.value);
        this.textboxLoadUnloadParam.enabled = value;
        if(!value){
            this.textboxLoadUnloadCount.clear();
            this.textboxLoadUnloadCount.resetValidation();
            this.textboxLoadUnloadCount.required = false;
            this.textboxLoadUnloadParam.clear();
            this.textboxLoadUnloadParam.required = false;
            this.textboxLoadingTime.clear();
            this.textboxUnloadingTime.clear();
        }
    }

    sourceDetentionHistoryLookupBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            event.filter.status = "!V";
            const dateRange = DateRange.parseDateRangeWithKeywords("t-5,t").getFormattedString().split("-");
            event.filter.creation_date =  `>=${dateRange[0]}&<=${dateRange[1]}`;
        }
    }
}
